import React, { useState, useEffect, useCallback } from 'react';
import { notificationAPI } from '../services/apiEndpoints';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { ScrollArea } from './ui/scroll-area';
import { Button } from './ui/button';
import {
    BellIcon,
    CheckCircledIcon,
    RocketIcon,
    LightningBoltIcon,
    ClockIcon,
} from '@radix-ui/react-icons';

const TYPE_ICON = {
    article_ready:     { Icon: CheckCircledIcon,  color: 'text-emerald-400' },
    article_published: { Icon: RocketIcon,        color: 'text-blue-400'   },
    trending_keyword:  { Icon: LightningBoltIcon, color: 'text-amber-400'  },
    inactivity:        { Icon: ClockIcon,         color: 'text-zinc-400'   },
};

const getRelativeTime = (dateString) => {
    if (!dateString) return '';
    const diff = Math.floor((Date.now() - new Date(dateString)) / 1000);
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
};

const NotificationBell = () => {
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount]     = useState(0);

    const fetchNotifications = useCallback(async () => {
        try {
            const res = await notificationAPI.getAll();
            setNotifications(res.data.notifications || []);
            setUnreadCount(res.data.unread_count || 0);
        } catch (e) {
            // non-critical
        }
    }, []);

    useEffect(() => {
        fetchNotifications();
        const timer = setInterval(fetchNotifications, 30_000);
        return () => clearInterval(timer);
    }, [fetchNotifications]);

    const handleMarkRead = async (id) => {
        try {
            await notificationAPI.markRead(id);
            setNotifications(prev =>
                prev.map(n => n.id === id ? { ...n, read_at: new Date().toISOString() } : n)
            );
            setUnreadCount(prev => Math.max(0, prev - 1));
        } catch (e) { /* non-critical */ }
    };

    const handleMarkAllRead = async () => {
        try {
            await notificationAPI.markAllRead();
            setNotifications(prev => prev.map(n => ({ ...n, read_at: n.read_at || new Date().toISOString() })));
            setUnreadCount(0);
        } catch (e) { /* non-critical */ }
    };

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button
                    variant="ghost"
                    size="icon"
                    className="relative text-zinc-400 hover:text-zinc-50 hover:bg-white/10"
                    aria-label="Notifications"
                >
                    <BellIcon className="h-5 w-5" />
                    {unreadCount > 0 && (
                        <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white leading-none">
                            {unreadCount > 9 ? '9+' : unreadCount}
                        </span>
                    )}
                </Button>
            </DropdownMenuTrigger>

            <DropdownMenuContent
                align="end"
                className="w-80 p-0 bg-zinc-900/95 backdrop-blur-xl border-white/10"
                sideOffset={8}
            >
                {/* Header */}
                <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
                    <span className="text-sm font-semibold text-zinc-200">Notifications</span>
                    {unreadCount > 0 && (
                        <button
                            onClick={handleMarkAllRead}
                            className="text-xs text-violet-400 hover:text-violet-300 transition-colors"
                        >
                            Mark all read
                        </button>
                    )}
                </div>

                {/* List */}
                <ScrollArea className="max-h-80">
                    {notifications.length === 0 ? (
                        <div className="py-8 text-center text-zinc-500 text-sm">
                            No notifications yet
                        </div>
                    ) : (
                        <div className="divide-y divide-white/5">
                            {notifications.slice(0, 10).map((n) => {
                                const { Icon, color } = TYPE_ICON[n.type] || TYPE_ICON.inactivity;
                                const isUnread = !n.read_at;
                                return (
                                    <div
                                        key={n.id}
                                        onClick={() => isUnread && handleMarkRead(n.id)}
                                        className={`flex gap-3 px-4 py-3 transition-colors ${
                                            isUnread
                                                ? 'bg-white/5 hover:bg-white/10 cursor-pointer'
                                                : 'hover:bg-white/5 cursor-default'
                                        }`}
                                    >
                                        <div className={`mt-0.5 flex-shrink-0 ${color}`}>
                                            <Icon className="h-4 w-4" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-xs font-medium text-zinc-200 leading-snug">{n.title}</p>
                                            <p className="text-xs text-zinc-500 mt-0.5 leading-snug">{n.message}</p>
                                            <p className="text-[10px] text-zinc-600 mt-1">{getRelativeTime(n.created_at)}</p>
                                        </div>
                                        {isUnread && (
                                            <span className="mt-1.5 flex-shrink-0 h-2 w-2 rounded-full bg-violet-500" />
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </ScrollArea>
            </DropdownMenuContent>
        </DropdownMenu>
    );
};

export default NotificationBell;
